import React, { useMemo, useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FormProvider,
  useForm,
  type SubmitHandler,
  type Resolver,
  useWatch,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { company, contact, product, paymentTop, paymentDates } from "./config";
import { schema, FormValues } from "./schema";
import {
  useAfterDiscount,
  calcExpirationDate,
} from "../../../utils/calculations";
import { useTimezone } from "../../../hooks/useTimezone";
import { useFieldConfig } from "../../../hooks/useFieldConfig";
import {
  useRegionList,
  useCountryList,
  useStateList,
  useCityList,
} from "../../../hooks/useLocationData";
import {
  addCustomer,
  fetchProducts,
  fetchCustomerPlans,
} from "../services/customerService";
import {
  SECTION_CARD_CLASS,
  CUSTOMER_DEFAULT_VALUES,
} from "../../../constants/mspAndCustomerForm";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setLoading,
  setError,
  setNotification,
  clearError,
  clearNotification,
} from "../../msp/slices/mspSlice";
import { FormHeader } from "../../../components/form/FormHeader";
import PaymentDetails from "../../../components/PaymentDetails";
import FormSection from "../../../components/FormSection";

type ProductOption = { label: string; value: string };

type PricingOption = {
  id: number;
  interval: string;
  unit_amount: number;
};

// Capitalize plan interval labels for UI (e.g., "monthly" -> "Monthly")
const cap = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

export default function AddCustomerForm({
  onCancel,
  onSuccess,
  mspId,
}: Readonly<{
  onCancel?: () => void;
  onSuccess?: () => void;
  mspId?: string;
}>) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, notification } = useAppSelector((state) => state.msp);

  // Local UI state (catalog + pricing helpers)
  const [productOpts, setProductOpts] = useState<ProductOption[]>([]);
  const [pricingOptions, setPricingOptions] = useState<PricingOption[]>([]);
  const [licenses, setLicenses] = useState<number | null>(null);

  // Form setup
  const defaultValues = useMemo(
    () => ({
      ...CUSTOMER_DEFAULT_VALUES,
      // Attach MSP id (when creating customer under an MSP context)
      msp_id: mspId ? Number.parseInt(mspId) : 0,
      // Licenses are managed separately and committed onBlur
      no_of_licenses: "" as any,
    }),
    [mspId]
  );

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema) as Resolver<FormValues>,
    mode: "onBlur",
    shouldUnregister: false,
    reValidateMode: "onChange",
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    register,
    control,
    setValue,
    trigger,
    formState: { errors, isSubmitting },
  } = methods;

  // Watches (used for dependent dropdowns and pricing calculations)
  const companyCountryId = useWatch({ control, name: "company_country_id" });
  const companyStateId = useWatch({ control, name: "company_state_id" });
  const contactCountryId = useWatch({ control, name: "country_id" });
  const contactStateId = useWatch({ control, name: "state_id" });
  const timezoneValue = useWatch({ control, name: "timezone" });
  const selectedProductId = useWatch({ control, name: "product" });

  // Payment-related watched fields
  const [totalPaid, discountVal] = useWatch({
    control,
    name: ["total_paid_amount", "discount"],
  }) as [number | undefined, number | undefined];

  const [billingFrequency] = useWatch({
    control,
    name: ["billing_frequency"],
  }) as [string | undefined];

  const [paymentDate] = useWatch({
    control,
    name: ["date_of_payment"],
  }) as [string | undefined];

  // Derived value shown in UI (Total - discount%)
  const afterDiscount = useAfterDiscount(totalPaid ?? 0, discountVal ?? 0);

  // Location dropdown data (region/country/state/city)
  const {
    regionList: regions,
    isRegionLoading,
    regionLoadingError,
  } = useRegionList(companyCountryId ? Number(companyCountryId) : undefined);

  const {
    countryList: countries,
    isCountryLoading,
    countryLoadingError,
  } = useCountryList();

  const {
    stateList: companyStates,
    isStateLoading: loadingCompanyStates,
    stateLoadingError: companyStateError,
  } = useStateList(companyCountryId);

  const {
    cityList: companyCities,
    isCityLoading: loadingCompanyCities,
    cityLoadingError: companyCityError,
  } = useCityList(companyStateId);

  const {
    stateList: contactStates,
    isStateLoading: loadingContactStates,
    stateLoadingError: contactStateError,
  } = useStateList(contactCountryId);

  const {
    cityList: contactCities,
    isCityLoading: loadingContactCities,
    cityLoadingError: contactCityError,
  } = useCityList(contactStateId);

  // Timezone dropdown options and suggestions based on selected location
  const { tzOptions, suggestedTimezone, localTimezone } = useTimezone(
    companyCountryId,
    companyStateId,
    timezoneValue,
    setValue,
    countries,
    companyStates
  );

  // Catalog: fetch products once on mount
  useEffect(() => {
    (async () => {
      try {
        const products: any[] = await fetchProducts();
        const opts = products.map((p: any) => ({
          label: p.product_name,
          value: String(p.id),
        }));
        setProductOpts(opts);
      } catch (e) {
        console.error("Failed to load products", e);
        dispatch(setError("Failed to load products"));
      }
    })();
  }, [dispatch]);

  // Catalog: fetch pricing plans when product changes
  // Resets licenses and pricing-related fields to prevent stale calculations
  useEffect(() => {
    // Reset any previously committed license count on product change
    setLicenses(null);
    setValue("no_of_licenses", undefined as any);

    // If product not selected, clear pricing fields
    if (!selectedProductId) {
      setPricingOptions([]);
      setValue("billing_frequency", "");
      setValue("total_paid_amount", 0);
      return;
    }

    (async () => {
      try {
        const fetchedPlans = await fetchCustomerPlans(
          Number(selectedProductId)
        );

        // If API returns prices, map them to dropdown options
        if (fetchedPlans.length > 0 && fetchedPlans[0].PlanPrice?.length > 0) {
          const opts: PricingOption[] = fetchedPlans[0].PlanPrice.map(
            (price: any) => ({
              id: Number(price.id),
              interval: cap(String(price.interval)),
              unit_amount: Number(price.unit_amount),
            })
          );

          setPricingOptions(opts);

          // Auto-select first frequency option for better UX
          if (opts.length > 0) {
            setValue("billing_frequency", opts[0].interval, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });

            // Total amount will be recalculated once licenses are entered
            setValue("total_paid_amount", 0);
          }
        } else {
          // No pricing available for the selected product
          setPricingOptions([]);
          setValue("billing_frequency", "");
          setValue("total_paid_amount", 0);
        }
      } catch (e) {
        console.error("Failed to load plans", e);
        dispatch(setError("Failed to load billing plans"));
      }
    })();
  }, [selectedProductId, setValue, dispatch]);

  // Preselect first region after region list loads
  useEffect(() => {
    if (regions.length > 0 && companyCountryId) {
      setValue("region_id", regions[0].id);
      trigger("region_id");
    }
  }, [regions, companyCountryId, setValue, trigger]);

  // Aggregate location API errors into a single error message
  useEffect(() => {
    const combinedError =
      regionLoadingError ||
      countryLoadingError ||
      companyStateError ||
      companyCityError ||
      contactStateError ||
      contactCityError ||
      null;

    if (combinedError) dispatch(setError(combinedError));
    else dispatch(clearError());
  }, [
    regionLoadingError,
    countryLoadingError,
    companyStateError,
    companyCityError,
    contactStateError,
    contactCityError,
    dispatch,
  ]);

  // Auto-clear toast/notification after a short delay
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => dispatch(clearNotification()), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification, dispatch]);

  // Small helpers for payload formatting
  const trim = (v: string) => v.trim();
  const optionalTrim = (v?: string) => v?.trim() ?? "";
  const onlyDate = (v?: string) => (v ? v.slice(0, 10) : "");

  // Pricing: resolve selected price from the chosen billing frequency
  const selectedPrice = useMemo(() => {
    if (!billingFrequency) return undefined;
    const freq = billingFrequency.toLowerCase();
    return pricingOptions.find((p) => p.interval.toLowerCase() === freq);
  }, [pricingOptions, billingFrequency]);

  // Pricing: auto-calc total amount = unit price * number of licenses
  useEffect(() => {
    if (!selectedPrice) {
      setValue("total_paid_amount", 0);
      return;
    }

    const licensesCount = Number(licenses || 0);
    const unitAmount = Number(selectedPrice.unit_amount || 0);

    if (!licensesCount || !unitAmount) {
      setValue("total_paid_amount", 0);
      return;
    }

    setValue("total_paid_amount", licensesCount * unitAmount);
  }, [selectedPrice, licenses, setValue]);

  // Payment: auto-calc expiration date from payment date + billing frequency
  useEffect(() => {
    if (!paymentDate || !billingFrequency) return;

    const exp = calcExpirationDate(paymentDate, billingFrequency);
    if (exp) {
      setValue("expiration_date", exp);
      trigger("expiration_date");
    }
  }, [paymentDate, billingFrequency, setValue, trigger]);

  // Submit handler: build payload and call API
  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async (data) => {
      try {
        dispatch(setLoading(true));
        dispatch(clearError());

        const price = pricingOptions.find(
          (p) =>
            p.interval.toLowerCase() === data.billing_frequency.toLowerCase()
        );
        if (!price) throw new Error("Please select a valid billing frequency.");

        const companyEmail = trim(data.company_email).toLowerCase();
        const primaryEmail = trim(data.corporate_email).toLowerCase();

        const payload = {
          company_name: trim(data.company_name),
          website: trim(data.website),
          tax_id: optionalTrim(data.tax_id),
          timezone: trim(data.timezone),
          region_id: data.region_id,
          plan_price_id: price.id,
          no_of_licenses: data.no_of_licenses,
          total_paid_amount: data.total_paid_amount,
          discount: data.discount || 0,
          payment_mode: trim(data.payment_mode),
          transaction_ref: optionalTrim(data.transaction_ref),
          date_of_payment: data.date_of_payment,
          expiration_date: onlyDate(data.expiration_date),
          comment: optionalTrim(data.comment),
          msp_id: data.msp_id,
          contacts: [
            {
              first_name: trim(data.company_name),
              last_name: null,
              corporate_email: companyEmail,
              designation: null,
              position: null,
              mobile_phone: optionalTrim(data.company_phone),
              work_phone_no: optionalTrim(data.company_work_phone_no),
              contact_type: "company" as const,
              addresses: [
                {
                  street_address: optionalTrim(data.company_street_address),
                  country: data.company_country_id,
                  state: data.company_state_id,
                  city: data.company_city_id,
                  postal_code: optionalTrim(data.company_postal_code),
                  is_default: true,
                },
              ],
            },
            {
              first_name: trim(data.first_name),
              last_name: trim(data.last_name),
              corporate_email: primaryEmail,
              designation: trim(data.designation),
              position: trim(data.position),
              mobile_phone: trim(data.mobile_phone),
              work_phone_no: optionalTrim(data.work_phone_no),
              contact_type: "primary" as const,
              addresses: [
                {
                  street_address: optionalTrim(data.street_address),
                  country: data.country_id,
                  state: data.state_id,
                  city: data.city_id,
                  postal_code: optionalTrim(data.postal_code),
                  is_default: false,
                },
              ],
            },
          ],
        };

        await addCustomer(payload);

        dispatch(
          setNotification({
            message: "Customer added successfully!",
            type: "success",
          })
        );
        reset();
        onSuccess?.();
        if (onCancel) onCancel();
        else navigate("/customers", { replace: true });
      } catch (error: any) {
        console.error("Failed to add customer:", error);
        dispatch(setError(error.message || "Failed to add customer"));
        dispatch(
          setNotification({
            message: error.message || "Failed to add customer",
            type: "error",
          })
        );
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, reset, onCancel, onSuccess, navigate, pricingOptions]
  );

  // Form field config: inject dynamic options and behavior for product/pricing fields
  const productConfigWithOptions = useMemo(() => {
    const isBillingLocked = licenses != null && licenses > 0;

    return product.map((f) => {
      if (f.name === "product") {
        return {
          ...f,
          options: [
            { label: "-- Choose Product --", value: "" },
            ...productOpts,
          ],
        };
      }

      if (f.name === "billing_frequency") {
        return {
          ...f,
          options: [
            { label: "-- Choose Frequency --", value: "" },
            ...pricingOptions.map((p) => ({
              label: p.interval,
              value: p.interval,
            })),
          ],
          disabled: isBillingLocked,
        };
      }

      if (f.name === "no_of_licenses") {
        return {
          ...f,
          // Commit licenses onBlur (prevents partial typing updates)
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            const raw = (e.target.value ?? "").toString().trim();
            const n = Number(raw);
            const lic = Number.isFinite(n) && n > 0 ? n : null;
            setLicenses(lic);
          },
          // Show "$X / per unit" suffix when a plan price is selected
          suffixText: selectedPrice
            ? () => (
                <>
                  ${selectedPrice.unit_amount}/
                  <span className="font-medium">per unit</span>
                </>
              )
            : undefined,
        };
      }

      return f;
    });
  }, [productOpts, pricingOptions, selectedPrice, licenses]);

  // Final field configs (location-aware) for company/contact sections
  const companyConfig = useFieldConfig(company, {
    regions,
    countries,
    companyStates,
    companyCities,
    tzOptions,
    suggestedTimezone,
    localTimezone,
    timezoneValue,
  });

  const contactConfig = useFieldConfig(contact, {
    countries,
    contactStates,
    contactCities,
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="p-6"
        autoComplete="off"
      >
        <FormHeader
          title="Add New Customer"
          subtitle="The Smart Way to Add your Customer"
          backLabel="Back to Customer List"
          onBack={onCancel}
        />

        <div className={SECTION_CARD_CLASS}>
          <FormSection title="Company Information" fields={companyConfig} />

          <div className="mt-10">
            <FormSection title="Primary Contact" fields={contactConfig} />
          </div>

          <div className="mt-12">
            <FormSection title="Product" fields={productConfigWithOptions} />
          </div>

          <PaymentDetails
            paymentTop={paymentTop}
            paymentDates={paymentDates}
            discountError={errors.discount?.message}
            afterDiscount={afterDiscount}
            discountRegister={register("discount", { valueAsNumber: true })}
          />

          <div className="mt-8 flex justify-center">
            <button
              type="submit"
              disabled={
                loading ||
                isSubmitting ||
                isRegionLoading ||
                isCountryLoading ||
                loadingCompanyStates ||
                loadingCompanyCities ||
                loadingContactStates ||
                loadingContactCities
              }
              className="inline-flex min-w-[180px] items-center justify-center rounded-[6px] bg-[#1D58E2] px-8 py-3 text-[15px] font-semibold text-white shadow hover:brightness-110 disabled:opacity-50 transition-all duration-200 hover:cursor-pointer"
            >
              {loading || isSubmitting ? "CREATING..." : "ADD NOW"}
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
